import {
  AppShell,
  Burger,
  Group,
  Text,
  NavLink,
  Tooltip,
  Menu,
  ScrollArea,
  UnstyledButton,
  Avatar,
  Divider,
  Box,
  Button,
  Flex,
  Image,
  Badge,
  Stack,
  Loader,
} from "@mantine/core";
import {
  IconLayoutGrid,
  IconHome,
  IconSearch,
  IconBriefcase,
  IconBookmark,
  IconHelpCircle,
  IconLogout,
  IconAlertTriangle,
  IconChevronRight,
  IconCoins,
  IconSettings,
  IconPencil,
} from "@tabler/icons-react";
import { useDisclosure } from "@mantine/hooks";
import { Outlet, useNavigate, useLocation } from "react-router-dom";
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type CSSProperties } from "react";
import { useUserContext } from "@/context/UserContext";
import { hasClientCreatedProfile } from "@/shared/constants/clientOnboarding.const";
import { SidebarOnboardingTour } from "@/components/layout/SidebarOnboardingTour";
import { SidebarDesktopFooter } from "@/components/layout/SidebarDesktopFooter";
import { NewRequestsSpotlight } from "@/components/layout/NewRequestsSpotlight";
import ApplyVerificationModal from "@/pages/profile-verification/ApplyVerificationModal";
import ProfileWelcomeCreditsModal from "@/pages/profile-verification/ProfileWelcomeCreditsModal";
import { PROFILE_WELCOME_CREDITS_KEY } from "@/shared/constants/onboarding.const";
import { normalizeIndianGstinInput } from "@shared/indianGstin";
import trpc from "@/shared/utils/trpc";
import {
  getTrpcErrorMessage,
  handleTrpcError,
  isIndiaGridlinesUserVerificationError,
} from "@/shared/utils/trpc/handletrpcError.util";
import { notifications } from "@mantine/notifications";

type AppLayoutNavContextValue = {
  mobileOpened: boolean;
  toggleMobile: () => void;
  closeMobile: () => void;
};

const AppLayoutNavContext = createContext<AppLayoutNavContextValue | null>(null);

export function useAppLayoutNav() {
  const ctx = useContext(AppLayoutNavContext);
  if (!ctx) throw new Error("useAppLayoutNav must be used within <AppLayout />");
  return ctx;
}

type VerificationModalContextValue = {
  openVerificationModal: () => void;
};

const VerificationModalContext = createContext<VerificationModalContextValue | null>(null);

export function useVerificationModal() {
  const ctx = useContext(VerificationModalContext);
  if (!ctx) throw new Error("useVerificationModal must be used within <AppLayout />");
  return ctx;
}

const noopOpenVerificationModal = () => {};

/** Like useVerificationModal but safe on public layouts (e.g. Discover on `/` or `/discover`). */
export function useVerificationModalOpener(): () => void {
  const ctx = useContext(VerificationModalContext);
  return ctx?.openVerificationModal ?? noopOpenVerificationModal;
}

interface ProfileContextType {
  isProfileCreated: boolean;
  setIsProfileCreated: (value: boolean) => void;
}

const ProfileContext = createContext<ProfileContextType>({
  isProfileCreated: false,
  setIsProfileCreated: () => { },
});

export const useProfileStatus = () => useContext(ProfileContext);
const PROFILE_VERIFICATION_LANDING_KEY = "gt:profileVerificationSummary";

// Company Status Banner Component
interface CompanyStatusBannerProps {
  adminBlocked: boolean;
  blockedReason?: string | null;
  companyStatus?: string;
  onViewMore: () => void;
}

function CompanyStatusBanner({ adminBlocked, blockedReason, companyStatus, onViewMore }: CompanyStatusBannerProps) {
  if (adminBlocked) {
    return (
      <Box
        pos="fixed"
        bottom={0}
        left={{ base: 0, sm: 300 }}
        right={0}
        py={16}
        px={32}
        style={{
          backgroundColor: "#FEE2E2",
          zIndex: 100,
          borderTop: "2px solid #FECACA",
        }}
      >
        <Group justify="space-between" align="center" wrap="nowrap">
          <Group gap="md" wrap="nowrap" style={{ flex: 1, minWidth: 0 }}>
            <IconAlertTriangle size={24} color="#991B1B" style={{ flexShrink: 0 }} />
            <Box style={{ minWidth: 0 }}>
              <Text size="md" fw={600} c="#991B1B">
                Your account has been temporarily blocked please contact support.
              </Text>
              <Text size="sm" c="#B91C1C" truncate>
                {blockedReason ||
                  "Your profile has been disabled. You will not be able to perform certain actions like shortlist, expressing interests, etc."}{" "}
              </Text>
            </Box>
          </Group>
          <Button
            variant="filled"
            color="#D84B4B"
            size="sm"
            rightSection={<IconChevronRight size={16} />}
            onClick={onViewMore}
            style={{ flexShrink: 0 }}
          >
            View more
          </Button>
        </Group>
      </Box>
    );
  }

  if (companyStatus === "ACTION_REQUIRED") {
    return (
      <Box
        pos="fixed"
        bottom={0}
        left={{ base: 0, sm: 300 }}
        right={0}
        py={16}
        px={32}
        style={{
          backgroundColor: "#FEE2E2",
          zIndex: 100,
          borderTop: "2px solid #FECACA",
        }}
      >
        <Group justify="space-between" align="center" wrap="nowrap">
          <Group gap="md" wrap="nowrap" style={{ flex: 1, minWidth: 0 }}>
            <IconAlertTriangle size={24} color="#991B1B" style={{ flexShrink: 0 }} />
            <Box style={{ minWidth: 0 }}>
              <Text size="md" fw={600} c="#991B1B">
                Your company could not be verified
              </Text>
              <Text size="sm" c="#B91C1C" truncate>
                Click to know more
              </Text>
            </Box>
          </Group>
          <Button
            variant="filled"
            color="#D84B4B"
            size="sm"
            rightSection={<IconChevronRight size={16} />}
            onClick={onViewMore}
            style={{ flexShrink: 0 }}
          >
            Go to Profile
          </Button>
        </Group>
      </Box>
    );
  }

  return null;
}

export function AppLayout() {
  const [mobileOpened, { toggle: toggleMobile, close: closeMobile }] = useDisclosure();
  const [desktopOpened] = useDisclosure(true);
  const [isProfileCreated, setIsProfileCreated] = useState(false);
  const { userDetails, logout, shortlistedIds, refetchUser, pendingRequestCounts } = useUserContext();
  const shortlistCount = shortlistedIds.size;
  const navigate = useNavigate();
  const location = useLocation();
  const companyId = userDetails?.company?.id;
  const companyStatus = userDetails?.company?.status;
  const hasCreatedProfile = hasClientCreatedProfile(userDetails);

  const [welcomeCreditsModalOpen, setWelcomeCreditsModalOpen] = useState(false);
  const [signupBonusCredits, setSignupBonusCredits] = useState(20);
  const [verificationModalOpen, setVerificationModalOpen] = useState(false);
  const [verificationData, setVerificationData] = useState<Record<string, unknown> | null>(null);
  const [verificationApplying, setVerificationApplying] = useState(false);
  const [verificationSubmitError, setVerificationSubmitError] = useState<string | null>(null);
  const [companyDetails, setCompanyDetails] = useState<any>(null);
  const [sidebarCreditAvailable, setSidebarCreditAvailable] = useState<number | null>(null);
  const [sidebarCreditLoadFailed, setSidebarCreditLoadFailed] = useState(false);
  const lastWalletUserIdRef = useRef<string | null>(null);
  const lastAutoOpenPathRef = useRef<string | null>(null);
  const welcomeCreditsGateRef = useRef(false);
  const openVerificationModal = useCallback(() => setVerificationModalOpen(true), []);

  const shouldForceVerification =
    Boolean(companyId) &&
    hasCreatedProfile &&
    companyStatus !== "VERIFIED" &&
    companyStatus !== "SYSTEM_VERIFIED" &&
    companyStatus !== "UNDER_REVIEW";

  const isVerificationExemptRoute =
    location.pathname === "/profile" ||
    location.pathname.startsWith("/profile/") ||
    location.pathname === "/settings" ||
    location.pathname === "/credits" ||
    location.pathname.startsWith("/credits/") ||
    location.pathname === "/invoices" ||
    location.pathname === "/invoices/wallet" ||
    location.pathname === "/two-factor/setup";

  const isActive = (path: string) => {
    return location.pathname === path || location.pathname.startsWith(`${path}/`);
  };

  const navItems = [
    { label: "Explore Companies", icon: IconSearch, path: "/my-discovery" },
    { label: "Dashboard", icon: IconHome, path: "/dashboard" },
    { label: "My Profile", icon: IconLayoutGrid, path: "/profile" },
    { label: "Shortlist", icon: IconBookmark, path: "/shortlist" },
    { label: "Collaborations", icon: IconBriefcase, path: "/collaborations" },
    // { label: "Invoices", icon: IconReceipt, path: "/invoices" },
  ];

  const handleNavClick = (path: string) => {
    if (location.pathname === "/create-profile") return;
    if (isActive(path)) {
      navigate(0);
      closeMobile();
      return;
    }
    navigate(path);
    closeMobile();
  };

  const isDashboard = location.pathname === "/create-profile";

  const sidebarCountBadgeStyles = (navActive: boolean): { root: CSSProperties; label: CSSProperties } => ({
    root: {
      backgroundColor: navActive ? "var(--mantine-color-primary-7)" : "#ffffff",
      color: navActive ? "#ffffff" : "var(--mantine-color-primary-7)",
      borderRadius: 9999,
      minWidth: 24,
      height: 24,
      padding: 0,
      paddingInline: 5,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      boxSizing: "border-box",
    },
    label: {
      fontSize: 12,
      fontWeight: 800,
      lineHeight: 1,
    },
  });

  const handleLogout = () => {
    logout();
  };

  // Company status banner handling
  const isAdminBlocked = userDetails?.company?.adminBlocked || false;
  const blockedReason = userDetails?.company?.blockedReason;
  const showStatusBanner = isAdminBlocked || (companyStatus === "ACTION_REQUIRED" && location.pathname !== "/profile");

  const handleStatusBannerClick = () => {
    if (isAdminBlocked) {
      navigate("/settings"); // Navigate to settings/support for blocked accounts
    } else if (companyStatus === "ACTION_REQUIRED") {
      navigate("/profile"); // Navigate to edit profile for action required
    }
  };

  const navCtx = useMemo<AppLayoutNavContextValue>(
    () => ({ mobileOpened, toggleMobile, closeMobile }),
    [mobileOpened, toggleMobile, closeMobile]
  );

  useEffect(() => {
    if (!companyId) {
      setCompanyDetails(null);
      return;
    }
    let mounted = true;
    trpc.company.getById
      .query({ companyId })
      .then((data) => {
        if (mounted) setCompanyDetails(data);
      })
      .catch(() => {
        if (mounted) setCompanyDetails(null);
      });
    return () => {
      mounted = false;
    };
  }, [companyId]);

  useEffect(() => {
    if (!userDetails?.id) return;
    let cancelled = false;
    const userIdChanged = lastWalletUserIdRef.current !== userDetails.id;
    lastWalletUserIdRef.current = userDetails.id;
    if (userIdChanged) {
      setSidebarCreditLoadFailed(false);
      setSidebarCreditAvailable(null);
    }

    const loadWallet = () => {
      trpc.wallet.getBalance
        .query()
        .then((res) => {
          if (!cancelled) {
            setSidebarCreditAvailable(res.available);
            setSidebarCreditLoadFailed(false);
          }
        })
        .catch((err) => {
          handleTrpcError(err);
          if (!cancelled) {
            setSidebarCreditLoadFailed(true);
            setSidebarCreditAvailable(null);
          }
        });
    };

    loadWallet();
    const onVisible = () => {
      if (document.visibilityState === "visible") loadWallet();
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      cancelled = true;
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [userDetails?.id, location.pathname]);

  // Refresh company details on modal open so country-based options are up-to-date
  // after users edit their profile (HQ country / registration fields).
  useEffect(() => {
    if (!verificationModalOpen) return;
    if (!companyId) return;
    let mounted = true;
    trpc.company.getById
      .query({ companyId })
      .then((data) => {
        if (mounted) setCompanyDetails(data);
      })
      .catch(() => {
        if (mounted) setCompanyDetails(null);
      });
    return () => {
      mounted = false;
    };
  }, [verificationModalOpen, companyId]);

  const isMyDiscoveryRoute =
    location.pathname === "/my-discovery" || location.pathname.startsWith("/my-discovery/");

  // One-time welcome credits modal after first profile create → /my-discovery.
  useEffect(() => {
    if (!isMyDiscoveryRoute) return;
    if (sessionStorage.getItem(PROFILE_WELCOME_CREDITS_KEY) !== "1") return;
    sessionStorage.removeItem(PROFILE_WELCOME_CREDITS_KEY);
    welcomeCreditsGateRef.current = true;
    setWelcomeCreditsModalOpen(true);
    trpc.paymentConfig.get
      .query()
      .then((config) => {
        setSignupBonusCredits(config.signupBonusCredits);
      })
      .catch(() => {
        // Keep default 20
      });
  }, [isMyDiscoveryRoute, location.pathname]);

  const openVerificationAfterWelcome = useCallback(() => {
    if (!shouldForceVerification || isVerificationExemptRoute) return;
    lastAutoOpenPathRef.current = location.pathname;
    setVerificationModalOpen(true);
  }, [shouldForceVerification, isVerificationExemptRoute, location.pathname]);

  const handleWelcomeCreditsClose = useCallback(() => {
    welcomeCreditsGateRef.current = false;
    setWelcomeCreditsModalOpen(false);
    openVerificationAfterWelcome();
  }, [openVerificationAfterWelcome]);

  // Auto-open on every navigation to non-exempt routes while unverified.
  useEffect(() => {
    if (!shouldForceVerification) return;
    if (isVerificationExemptRoute) return;
    if (sessionStorage.getItem(PROFILE_WELCOME_CREDITS_KEY) === "1") return;
    if (welcomeCreditsGateRef.current || welcomeCreditsModalOpen) return;
    if (lastAutoOpenPathRef.current === location.pathname) return;
    lastAutoOpenPathRef.current = location.pathname;
    setVerificationModalOpen(true);
  }, [shouldForceVerification, isVerificationExemptRoute, location.pathname, welcomeCreditsModalOpen]);

  const handleCloseVerificationModal = () => {
    setVerificationModalOpen(false);
    setVerificationData(null);
    setVerificationSubmitError(null);
  };

  const handleApplyVerification = async (payload: { registrationIdType: string; registrationNumber: string }) => {
    if (!companyId || !companyDetails) return;
    const hqCountry = (
      companyDetails.headquartersCountry ||
      userDetails?.company?.headquartersCountry ||
      ""
    ).trim();
    setVerificationSubmitError(null);
    setVerificationApplying(true);
    try {
      const hqTrim = hqCountry;
      const usesLei = payload.registrationIdType === "LEI";
      const usesUsccShape =
        payload.registrationIdType === "USCC" ||
        (payload.registrationIdType === "COUNTRY_REGISTRATION" && hqTrim === "China (Mainland)");
      const usesGst = payload.registrationIdType === "GST";
      const usesDigitsOnlyType = payload.registrationIdType === "NOGN" || payload.registrationIdType === "SIREN";
      const normalizedRegistrationNumber = usesLei || usesUsccShape
        ? payload.registrationNumber.trim().toUpperCase()
        : usesGst
          ? normalizeIndianGstinInput(payload.registrationNumber)
          : usesDigitsOnlyType
            ? payload.registrationNumber.replace(/\D/g, "")
            : payload.registrationNumber.trim();

      await trpc.company.update.mutate({
        companyId,
        data: {
          registrationIdType: payload.registrationIdType,
          registrationNumber: normalizedRegistrationNumber || null,
        },
      });

      const result = await trpc.verification.verifyCompany.mutate({
        legalName: companyDetails.legalName,
        registrationNumber: normalizedRegistrationNumber,
        headquartersCountry: companyDetails.headquartersCountry,
        state: companyDetails.state || undefined,
      });

      if (result?.success && "data" in result && result.data) {
        const data = result.data as Record<string, unknown>;
        const nextVerificationData = {
          ...data,
          _verificationSource: (data.verificationSource as string | undefined) ?? result.source,
        };
        setVerificationData(nextVerificationData);
        localStorage.setItem(
          "my-profile:last-system-verification",
          JSON.stringify({
            companyId,
            data: nextVerificationData,
          })
        );
        await refetchUser();
      } else if (result && !result.success && "pending24h" in result && (result as any).pending24h) {
        notifications.show({
          title: "Verification pending admin review",
          message:
            (result as { message?: string }).message ||
            "Please wait up to 24 hours. Our team will review your registration. You will be notified when your profile is verified.",
          color: "blue",
        });
        await refetchUser();
        handleCloseVerificationModal();
      }
    } catch (err) {
      if (isIndiaGridlinesUserVerificationError(err, hqCountry, payload.registrationIdType)) {
        setVerificationSubmitError(getTrpcErrorMessage(err));
        return;
      }
      handleTrpcError(err);
      handleCloseVerificationModal();
    } finally {
      setVerificationApplying(false);
    }
  };

  const verificationCtx = useMemo<VerificationModalContextValue>(
    () => ({ openVerificationModal }),
    [openVerificationModal],
  );

  return (
    <ProfileContext.Provider value={{ isProfileCreated, setIsProfileCreated }}>
      <AppLayoutNavContext.Provider value={navCtx}>
      <VerificationModalContext.Provider value={verificationCtx}>
      <AppShell
        header={{ height: { base: 60, sm: 0 } }}
        navbar={{
          width: 300,
          breakpoint: "sm",
          collapsed: { mobile: !mobileOpened, desktop: !desktopOpened },
        }}
        padding="0"
      >
        <AppShell.Header hiddenFrom="sm" style={{ zIndex: 1000 }}>
          <Group h="100%" px="md" justify="space-between" wrap="nowrap">
            <Image src="/gtLogo.svg" alt="GTsetu" style={{ height: 40, width: "auto", flexShrink: 0 }} />
            <Burger
              opened={mobileOpened}
              onClick={toggleMobile}
              size="sm"
              color="var(--mantine-color-primary-7)"
              style={{ flexShrink: 0 }}
            />
          </Group>
        </AppShell.Header>

        <AppShell.Navbar
          p="md"
          style={{
            backgroundColor: "var(--mantine-color-primary-6)",
            backgroundImage:
              "repeating-linear-gradient(0deg, rgba(255,255,255,0.06) 0, rgba(255,255,255,0.06) 1px, transparent 1px, transparent 40px), repeating-linear-gradient(90deg, rgba(255,255,255,0.06) 0, rgba(255,255,255,0.06) 1px, transparent 1px, transparent 40px)",
            backgroundSize: "auto",
            backgroundPosition: "top left",
            borderRight: "1px solid rgba(255, 255, 255, 0.14)",
          }}
        >
          {/* HEADER / BRANDING */}
          <AppShell.Section mb="md" visibleFrom="sm">
            <Group justify="center" align="center">
              <Flex align="center">
                <Image
                  src="/gtLogo.svg"
                  alt="GTsetu"
                  h={60}
                  w="auto"
                  style={{ filter: "brightness(0) invert(1)" }}
                />
              </Flex>

              <Burger
                opened={mobileOpened}
                onClick={toggleMobile}
                hiddenFrom="sm"
                size="sm"
                color="var(--mantine-color-primary-7)"
              />
            </Group>

            <Divider mt="sm" color="rgba(255, 255, 255, 0.9)" />
          </AppShell.Section>

          {/* NAV LINKS */}
          <AppShell.Section grow component={ScrollArea}>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {navItems.map((item) => {
                const disabled = isDashboard || companyStatus === "UNDER_REVIEW";
                const active = isActive(item.path);
                const sidebarTourId = item.path.replace(/^\//, "");

                return (
                  <Box key={item.label} data-sidebar-tour={sidebarTourId}>
                    <Tooltip
                      label={
                        companyStatus === "UNDER_REVIEW"
                          ? "Your profile is under review. Navigation is temporarily disabled."
                          : "Complete your profile to unlock this feature"
                      }
                      disabled={!disabled}
                      position="right"
                      withArrow
                    >
                      <NavLink
                        label={
                          <Group justify="space-between" style={{ width: "100%" }}>
                            <Text
                              size="sm"
                              fw={600}
                              className="sidebar-nav-text"
                              c={active ? "primary.7" : "white"}
                            >
                              {item.label}
                            </Text>

                            {item.path === "/shortlist" && shortlistCount > 0 && (
                              <Badge size="xs" styles={sidebarCountBadgeStyles(active)}>
                                {shortlistCount}
                              </Badge>
                            )}
                            {item.path === "/dashboard" && pendingRequestCounts.received > 0 && (
                              <Badge
                                size="xs"
                                styles={sidebarCountBadgeStyles(active)}
                                aria-label={`${pendingRequestCounts.received} new request${
                                  pendingRequestCounts.received === 1 ? "" : "s"
                                }`}
                              >
                                {pendingRequestCounts.received}
                              </Badge>
                            )}
                          </Group>
                        }
                        leftSection={
                          <item.icon
                            size="1.2rem"
                            stroke={1.8}
                            color={active ? "var(--mantine-color-primary-7)" : "white"}
                          />
                        }
                        active={active}
                        onClick={() => handleNavClick(item.path)}
                        disabled={disabled}
                        variant="subtle"
                        styles={{
                          root: {
                            borderRadius: 8,
                            padding: "10px 12px",
                            opacity: disabled ? 0.5 : 1,
                            cursor: disabled ? "not-allowed" : "pointer",
                            backgroundColor: active ? "#ffc300" : "transparent",
                            color: active ? "var(--mantine-color-primary-7)" : "white",
                            "&:hover": {
                              backgroundColor: "#ffc300",
                              color: "var(--mantine-color-primary-7)",
                              "& svg": {
                                color: "var(--mantine-color-primary-7)",
                              },
                              "& .sidebar-nav-text": {
                                color: "var(--mantine-color-primary-7)",
                              },
                            },
                          },
                          label: {
                            color: "inherit",
                          },
                        }}
                      />
                    </Tooltip>
                  </Box>
                );
              })}
            </div>
          </AppShell.Section>

          {/* SIDEBAR FOOTER — desktop card layout */}
          <AppShell.Section visibleFrom="sm">
            <SidebarDesktopFooter
              creditAvailable={sidebarCreditAvailable}
              creditLoadFailed={sidebarCreditLoadFailed}
              companyName={userDetails?.company?.legalName || "Company Name"}
              gtid={userDetails?.company?.gtid || "User"}
              companyStatus={companyStatus}
              onLogout={handleLogout}
            />
          </AppShell.Section>

          {/* SIDEBAR FOOTER — mobile compact layout */}
          <AppShell.Section hiddenFrom="sm">
            <Stack gap="sm">
              <UnstyledButton
                w="100%"
                px="xs"
                py={4}
                onClick={() => navigate("/credits")}
                style={{
                  borderRadius: 8,
                  textAlign: "left",
                  transition: "background-color 0.2s",
                }}
                onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "rgba(255, 255, 255, 0.12)")}
                onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "transparent")}
              >
                <Text c="white" size="xs" style={{ opacity: 0.85 }}>
                  Available Credit
                </Text>
                {sidebarCreditLoadFailed ? (
                  <Text size="sm" fw={600} c="white">
                    —
                  </Text>
                ) : sidebarCreditAvailable === null ? (
                  <Loader size="xs" color="white" type="dots" />
                ) : (
                  <Text size="lg" fw={600} c="white" lh={1.2}>
                    {sidebarCreditAvailable}
                  </Text>
                )}
              </UnstyledButton>
              <Menu shadow="md" width="target" position="top">
              <Menu.Target>
                <UnstyledButton
                  w="100%"
                  p="xs"
                  style={{
                    borderRadius: 8,
                    transition: "background-color 0.2s",
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "rgba(255, 255, 255, 0.12)")}
                  onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "transparent")}
                >
                  <Group>
                    <Avatar
                      radius="xl"
                      style={{
                        backgroundColor: "white",
                        border: "1px solid rgba(255, 255, 255, 0.18)",
                      }}
                    >
                      <IconSettings size={18} color="var(--mantine-color-primary-7)" />
                    </Avatar>
                    <div style={{ flex: 1, overflow: "hidden" }}>
                      <Text size="sm" fw={600} c="white" truncate>
                        {userDetails?.company?.legalName || "Company Name"}
                      </Text>
                      <Text c="white" size="xs" truncate style={{ opacity: 0.85 }}>
                        {userDetails?.company?.gtid || "User"}
                      </Text>
                    </div>
                  </Group>
                </UnstyledButton>
              </Menu.Target>
              <Menu.Dropdown
                style={{
                  backgroundColor: "var(--mantine-color-primary-6)",
                  border: "1px solid rgba(255, 255, 255, 0.14)",
                }}
              >
                <Menu.Item
                  c="white"
                  fw={600}
                  leftSection={<IconPencil size={16} />}
                  onClick={() => {
                    if (companyStatus === "UNDER_REVIEW") {
                      sessionStorage.setItem(
                        PROFILE_VERIFICATION_LANDING_KEY,
                        JSON.stringify({ kind: "EDIT_LOCKED_UNDER_REVIEW" })
                      );
                      navigate("/profile", {
                        replace: true,
                        state: { lockNoticeAt: Date.now() },
                      });
                      return;
                    }
                    navigate("/profile/edit");
                  }}
                  style={{ borderRadius: 8 }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = "#ffc300";
                    e.currentTarget.style.color = "var(--mantine-color-primary-7)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = "transparent";
                    e.currentTarget.style.color = "white";
                  }}
                >
                  Edit Profile
                </Menu.Item>
                <Menu.Item
                  c="white"
                  fw={600}
                  leftSection={<IconCoins size={16} />}
                  onClick={() => navigate("/credits")}
                  style={{ borderRadius: 8 }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = "#ffc300";
                    e.currentTarget.style.color = "var(--mantine-color-primary-7)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = "transparent";
                    e.currentTarget.style.color = "white";
                  }}
                >
                  Credits
                </Menu.Item>
                <Menu.Item
                  c="white"
                  fw={600}
                  leftSection={<IconHelpCircle size={16} />}
                  onClick={() => navigate("/settings")}
                  style={{ borderRadius: 8 }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = "#ffc300";
                    e.currentTarget.style.color = "var(--mantine-color-primary-7)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = "transparent";
                    e.currentTarget.style.color = "white";
                  }}
                >
                  Settings
                </Menu.Item>
                <Menu.Item
                  c="white"
                  fw={600}
                  leftSection={<IconLogout size={16} />}
                  onClick={handleLogout}
                  style={{ borderRadius: 8 }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = "#ffc300";
                    e.currentTarget.style.color = "var(--mantine-color-primary-7)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = "transparent";
                    e.currentTarget.style.color = "white";
                  }}
                >
                  Logout
                </Menu.Item>
              </Menu.Dropdown>
            </Menu>
            </Stack>
          </AppShell.Section>
        </AppShell.Navbar>

        <AppShell.Main>
          <Box style={{ paddingBottom: showStatusBanner ? 100 : 0 }}>
            <Outlet />
          </Box>
          <CompanyStatusBanner
            adminBlocked={isAdminBlocked}
            blockedReason={blockedReason}
            companyStatus={location.pathname.startsWith("/profile") ? undefined : companyStatus}
            onViewMore={handleStatusBannerClick}
          />
          <SidebarOnboardingTour />
          <NewRequestsSpotlight />
          <ProfileWelcomeCreditsModal
            opened={welcomeCreditsModalOpen}
            onClose={handleWelcomeCreditsClose}
            onGetVerified={handleWelcomeCreditsClose}
            signupBonusCredits={signupBonusCredits}
          />
          <ApplyVerificationModal
            opened={verificationModalOpen}
            onClose={handleCloseVerificationModal}
            onApply={handleApplyVerification}
            isLoading={verificationApplying}
            verificationData={verificationData}
            headquartersCountry={companyDetails?.headquartersCountry || userDetails?.company?.headquartersCountry || ""}
            initialRegistrationIdType={companyDetails?.registrationIdType || null}
            initialRegistrationNumber={companyDetails?.registrationNumber || ""}
            submitError={verificationSubmitError}
            onClearSubmitError={() => setVerificationSubmitError(null)}
          />
        </AppShell.Main>
      </AppShell>
      </VerificationModalContext.Provider>
      </AppLayoutNavContext.Provider>
    </ProfileContext.Provider>
  );
}
